/**
 * Centralized redirect utilities for login success handling
 * Handles redirection after successful authentication with proper SSO flow
 */

import { isValidReturnUrl } from "./helpers";

/**
 * Store return URL in sessionStorage for later retrieval
 */
export function storeReturnUrl(returnUrl: string): void {
  if (returnUrl && isValidReturnUrl(returnUrl)) {
    sessionStorage.setItem("auth_return_url", returnUrl);
  }
}

/**
 * Retrieve and clear return URL from sessionStorage
 */
export function retrieveAndClearReturnUrl(): string | null {
  const returnUrl = sessionStorage.getItem("auth_return_url");
  if (returnUrl) {
    sessionStorage.removeItem("auth_return_url");
  }
  return returnUrl;
}

/**
 * Get return URL from current URL search params
 */
export function getReturnUrlFromParams(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("returnUrl");
}

/**
 * Check if URL is external (different origin)
 */
export function isExternalUrl(url: string): boolean {
  if (url.startsWith('/')) {
    return false;
  }
  
  try {
    const targetUrl = new URL(url, window.location.origin);
    return targetUrl.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Perform redirect with optional delay for loading state
 */
export function performRedirect(url: string, delayMs: number = 0): void {
  const redirect = () => {
    window.location.href = url;
  };
  
  if (delayMs > 0) {
    setTimeout(redirect, delayMs);
  } else {
    redirect();
  }
}

/**
 * Handle post-callback redirect
 * Used after OAuth callback to redirect to the final destination
 */
export function handlePostCallbackRedirect(): void {
  const returnUrl = retrieveAndClearReturnUrl();
  
  if (returnUrl && isValidReturnUrl(returnUrl)) {
    performRedirect(returnUrl);
  } else {
    performRedirect("/success");
  }
}
